/**
 * EntityFormSection component.
 *
 * Groups related fields into a visual section within an EntityForm.
 * Supports two rendering modes:
 *
 * 1. **Card mode** (default): Renders inside a `Card` compound component
 *    with optional collapsible header.
 * 2. **Fieldset mode** (`card={false}`): Renders a native `<fieldset>`
 *    with a `<legend>` for accessibility.
 *
 * Title and description are resolved with a two-tier priority:
 *
 * 1. Props (`title` / `description`) take highest precedence.
 * 2. Config section-level values (`sectionConfig?.title` / `sectionConfig?.description`).
 * 3. Omitted entirely when neither source provides a value.
 *
 * Sections with conditions are evaluated against the current form values.
 * When a section is hidden by its conditions, it is unmounted (returns `null`)
 * rather than hidden via CSS.
 *
 * @see Spec section 2.6 for SectionConfig.
 * @see Spec section 3.4 for the EntityFormSection contract.
 */

import * as React from 'react';
import { Card } from '@components/base/layout/Card/Card';
import { Text } from '@components/base/display/Text/Text';
import { useEntityFormContext } from '../context/EntityFormContext';
import { evaluateConditions } from '../utils/conditionEvaluator';
import type { EntityFormSectionProps, SectionConfig } from '../types';

// ---------------------------------------------------------------------------
// Section Config Lookup
// ---------------------------------------------------------------------------

/**
 * Finds the matching `SectionConfig` from `config.sections` by section ID.
 *
 * @param sections - The array of section configs from the form config.
 * @param id       - The section ID to look up.
 * @returns The matching section config, or `undefined` if not found.
 */
function findSectionConfig(
  sections: SectionConfig[] | undefined,
  id: string,
): SectionConfig | undefined {
  return sections?.find((s) => s.id === id);
}

// ---------------------------------------------------------------------------
// Text Resolution Helpers
// ---------------------------------------------------------------------------

/**
 * Resolves the title from props or section config.
 *
 * @param propsTitle   - Title provided directly via component props.
 * @param configTitle  - Title from the section config.
 * @returns The resolved title, or `undefined` when neither source provides one.
 */
function resolveTitle(
  propsTitle: string | undefined,
  configTitle: string | undefined,
): string | undefined {
  return propsTitle ?? configTitle;
}

/**
 * Resolves the description from props or section config.
 *
 * @param propsDescription   - Description provided directly via component props.
 * @param configDescription  - Description from the section config.
 * @returns The resolved description, or `undefined` when neither source provides one.
 */
function resolveDescription(
  propsDescription: string | undefined,
  configDescription: string | undefined,
): string | undefined {
  return propsDescription ?? configDescription;
}

// ---------------------------------------------------------------------------
// Minimal base config for section condition evaluation
// ---------------------------------------------------------------------------

/**
 * A minimal FieldConfig used as the base config when evaluating
 * section-level conditions via `evaluateConditions`. Sections default
 * to visible, not required, and not disabled.
 */
const SECTION_BASE_CONFIG = {
  type: 'text' as const,
  label: '',
  visible: true,
  disabled: false,
};

// ---------------------------------------------------------------------------
// EntityFormSection Component
// ---------------------------------------------------------------------------

/**
 * Section compound component for EntityForm.
 *
 * Groups related fields into a visual section rendered as either a `Card`
 * (default) or a native `<fieldset>`. Supports conditional visibility
 * and collapsible Card headers.
 *
 * Must be rendered inside an `<EntityForm>` provider.
 *
 * @example
 * ```tsx
 * <EntityForm config={config}>
 *   <EntityForm.Section id="personal-info">
 *     <EntityForm.Field name="firstName" />
 *     <EntityForm.Field name="lastName" />
 *   </EntityForm.Section>
 * </EntityForm>
 * ```
 *
 * @example
 * ```tsx
 * // Fieldset mode for accessible grouping without Card styling
 * <EntityForm config={config}>
 *   <EntityForm.Section id="address" card={false}>
 *     <EntityForm.Field name="street" />
 *     <EntityForm.Field name="city" />
 *   </EntityForm.Section>
 * </EntityForm>
 * ```
 *
 * @example
 * ```tsx
 * // Override title and description from config
 * <EntityForm config={config}>
 *   <EntityForm.Section id="billing" title="Billing Address" description="Enter your billing info.">
 *     <EntityForm.Field name="billingStreet" />
 *   </EntityForm.Section>
 * </EntityForm>
 * ```
 */
export function EntityFormSection({
  id,
  title: propsTitle,
  description: propsDescription,
  card,
  children,
  className,
}: Readonly<EntityFormSectionProps>): React.ReactElement | null {
  const { config, form } = useEntityFormContext();

  // --- Look up section config ---

  const sectionConfig = findSectionConfig(config.sections, id);

  // --- Evaluate section-level conditions ---
  //
  // `conditions` accepts the legacy array shape OR a single node (a
  // ConditionGroup or SimpleCondition) after the phase-2 backport. An
  // empty array still means "no conditions", so we explicitly skip the
  // evaluation in that case.
  const sectionConditions = sectionConfig?.conditions;
  const hasSectionConditions =
    sectionConditions !== undefined &&
    (!Array.isArray(sectionConditions) || sectionConditions.length > 0);

  if (hasSectionConditions) {
    const result = evaluateConditions(
      sectionConditions,
      form.values,
      SECTION_BASE_CONFIG,
    );
    if (!result.visible) {
      return null;
    }
  }

  // --- Resolve text values ---

  const resolvedTitle = resolveTitle(propsTitle, sectionConfig?.title);
  const resolvedDescription = resolveDescription(
    propsDescription,
    sectionConfig?.description,
  );

  // --- Build container class name ---

  const containerClasses = [
    config.cssOverrides?.section,
    sectionConfig?.className,
    className,
  ]
    .filter(Boolean)
    .join(' ') || undefined;

  // --- Fieldset mode (card={false}) ---

  if (card === false) {
    return (
      <fieldset
        className={containerClasses}
        data-testid={`entity-form-section-${id}`}
      >
        {resolvedTitle && (
          <legend className="mb-3">
            <Text as="h3" size="lg" weight="semibold">
              {resolvedTitle}
            </Text>
            {resolvedDescription && (
              <Text color="muted" size="sm">
                {resolvedDescription}
              </Text>
            )}
          </legend>
        )}
        {children}
      </fieldset>
    );
  }

  // --- Card mode (default) ---

  return (
    <Card
      className={containerClasses}
      data-testid={`entity-form-section-${id}`}
    >
      {resolvedTitle && (
        <Card.Header
          title={resolvedTitle}
          subtitle={resolvedDescription}
          collapsible={sectionConfig?.collapsible}
          defaultExpanded={
            sectionConfig?.collapsible
              ? !sectionConfig.defaultCollapsed
              : undefined
          }
        />
      )}
      <Card.Content>{children}</Card.Content>
    </Card>
  );
}

EntityFormSection.displayName = 'EntityFormSection';
